import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Award, Copy, Check, Ban, RotateCcw, AlertTriangle, TrendingDown, Mail, BarChart3 } from 'lucide-react';
import {
  BADGE_FONT_PX, CARD_SURFACES, QUANT_STYLE, attributionStyle, blockerStyle, statusStyle,
} from './proofBankTokens';

const API = '/api';

// Colours are opaque hex from proofBankTokens, applied inline and guarded by
// cadenceBadges.contrast.test.js. The previous version used dark-theme Tailwind alpha
// utilities (text-amber-300 on bg-amber-400/10 and friends) on a #FFFFFF surface, which
// composited to near-invisible — and these badges carry the REASON a proof is unusable, so an
// illegible one puts the reader straight back to guessing.
function Badge({ fg, bg, children, testid, title }) {
  return (
    <span
      data-testid={testid}
      title={title}
      className="font-sans font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 shrink-0"
      style={{ color: fg, backgroundColor: bg, fontSize: `${BADGE_FONT_PX}px`, lineHeight: 1.4 }}
    >
      {children}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function num(v) {
  return v === null || v === undefined ? '—' : String(v);
}

export default function ProofBank() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usableOnly, setUsableOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [minDelta, setMinDelta] = useState('');
  const [fitterFilter, setFitterFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [draft, setDraft] = useState(null);   // { proof, ...draft } — the permission email

  const reload = useCallback(async () => {
    setError('');
    try {
      const p = new URLSearchParams();
      if (usableOnly) p.set('usable_only', 'true');
      if (minDelta !== '') p.set('min_delta', minDelta);
      const res = await axios.get(`${API}/proof-bank?${p.toString()}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the proof bank.');
    }
  }, [usableOnly, minDelta]);

  useEffect(() => { (async () => { setLoading(true); await reload(); setLoading(false); })(); }, [reload]);

  const copy = async (p) => {
    try {
      await navigator.clipboard.writeText(p.testimonial_text || '');
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch { setError('Could not copy to the clipboard.'); }
  };

  const markUsed = async (p) => {
    // eslint-disable-next-line no-alert
    const where = window.prompt('Where was it used? (e.g. "Instagram carousel, 12 Aug")');
    if (!where) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/proof-bank/${p.id}/use`, { where });
      await reload();
    } catch (e) { setError(e.response?.data?.detail || 'Could not mark it used.'); }
    finally { setBusyId(null); }
  };

  const withdraw = async (p) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Withdrawing consent — why? (kept on the record)');
    if (reason === null) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/proof-bank/${p.id}/withdraw`, { reason });
      await reload();
    } catch (e) { setError(e.response?.data?.detail || 'Could not withdraw.'); }
    finally { setBusyId(null); }
  };

  const restore = async (p) => {
    setBusyId(p.id);
    try {
      await axios.post(`${API}/proof-bank/${p.id}/restore`, {});
      await reload();
    } catch (e) { setError(e.response?.data?.detail || 'Could not restore.'); }
    finally { setBusyId(null); }
  };

  // --- request attribution -------------------------------------------------
  // Three deliberate steps: draft, then log that YOU sent it, then record the reply. The Hub
  // never sends this — an automated permission request is a contradiction in terms — so the
  // draft is copied into a mail client by a human.
  const openDraft = async (p) => {
    setBusyId(p.id);
    try {
      const res = await axios.get(`${API}/proof-bank/${p.id}/attribution-request/draft`);
      setDraft({ ...res.data, proof: p });
    } catch (e) { setError(e.response?.data?.detail || 'Could not draft the request.'); }
    finally { setBusyId(null); }
  };

  const logAsk = async () => {
    if (!draft?.proof) return;
    setBusyId(draft.proof.id);
    try {
      await axios.post(`${API}/proof-bank/${draft.proof.id}/attribution-request`, { channel: 'email' });
      setDraft(null);
      await reload();
    } catch (e) { setError(e.response?.data?.detail || 'Could not log the ask.'); }
    finally { setBusyId(null); }
  };

  const recordAnswer = async (p, outcome) => {
    setBusyId(p.id);
    try {
      await axios.post(`${API}/proof-bank/${p.id}/attribution-request/answer`, { outcome });
      await reload();
    } catch (e) { setError(e.response?.data?.detail || 'Could not record the answer.'); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="proofbank-loading">
        <Loader2 className="animate-spin text-hg-green" />
      </div>
    );
  }

  const proofs = data?.proofs || [];
  const summary = data?.summary || {};
  const fitters = Array.from(new Set(proofs.map((p) => p.fitter_name).filter(Boolean)));
  const shown = proofs.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (fitterFilter !== 'all' && (p.fitter_name || '') !== fitterFilter) return false;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="proofbank-page">
      {/* ---- the permission email. A DRAFT: the Hub does not send it, and the "log the ask"
              button is a separate, explicit click that means "I have sent this", not "I
              considered sending this". A log that records intent reads as evidence of an ask
              nobody made. */}
      {draft && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => setDraft(null)} data-testid="proof-draft-modal">
          <div className="bg-hg-surface border border-hg-border rounded-sm max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg text-hg-text tracking-wide">Ask for naming permission</h3>
            {!draft.eligible ? (
              <p className="mt-3 text-sm font-sans" style={{ color: '#991B1B' }} data-testid="proof-draft-blocked">
                {draft.blocked_reason}
              </p>
            ) : (
              <>
                <p className="mt-1 text-xs font-sans text-hg-text-dim">
                  To {draft.to} · from {draft.from_label} · would appear as{' '}
                  <strong className="text-hg-text">{draft.would_appear_as}</strong>
                </p>
                <div className="mt-3 text-xs font-sans text-hg-text-dim">Subject</div>
                <div className="text-sm font-sans text-hg-text">{draft.subject}</div>
                <div className="mt-3 text-xs font-sans text-hg-text-dim">Body</div>
                <pre className="mt-1 text-[13px] font-sans text-hg-text whitespace-pre-wrap bg-hg-bg border border-hg-border rounded-sm p-3"
                  data-testid="proof-draft-body">{draft.body}</pre>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => { navigator.clipboard?.writeText(draft.body); }}
                    data-testid="proof-draft-copy"
                    className="inline-flex items-center gap-1 text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green">
                    <Copy size={12} /> Copy body
                  </button>
                  <button
                    onClick={logAsk}
                    data-testid="proof-draft-log"
                    className="text-[11px] font-sans uppercase tracking-wider rounded-sm px-3 py-1 bg-hg-green text-white hover:bg-hg-green-hover">
                    I've sent it — log the ask
                  </button>
                  <span className="text-[11px] font-sans text-hg-text-dim">
                    The Hub does not send this. Copy it into your mail client, then log it.
                  </span>
                </div>
              </>
            )}
            <button onClick={() => setDraft(null)}
              className="mt-4 text-[11px] font-sans uppercase tracking-wider text-hg-text-dim hover:text-hg-text">
              Close
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">Proof Bank</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          What customers actually told us, and what we are allowed to repeat. Nothing here is usable
          in marketing without consent — and consent to quote is not consent to name.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-sm px-4 py-2 text-sm font-sans" data-testid="proofbank-error">
          {error}
        </div>
      )}

      {/* ---- what the bank actually holds, split by the permission it needs. One conflated
              "usable" number is what made a bank of 17 read as empty when every record was
              fine for aggregate use. */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="proofbank-summary">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ['Records', summary.records, null],
            ['Usable for aggregate stats', summary.quantitative_usable, 'no naming permission needed'],
            ['Quotable comments', summary.usable_quotes, 'comment permission + enough text'],
            ['Quotable WITH a name', summary.usable_attributed_quotes, 'both permissions'],
          ].map(([label, val, hint]) => (
            <div key={label}>
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">{label}</div>
              <div className="font-display text-2xl text-hg-text">{val === undefined ? '—' : String(val)}</div>
              {hint && <div className="text-[10px] font-sans text-hg-text-dim mt-0.5">{hint}</div>}
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-hg-border">
          <p className="text-xs font-sans text-hg-text-dim leading-relaxed" data-testid="proofbank-usage-note">
            {data?.usage_note}
          </p>
        </div>

        {/* Derived FIGURES render from summary.figures, where each number carries its own
            provenance in the same object. The label is not a caption the UI remembers to add
            — it arrives attached, so a number cannot be shown without it. The website audit
            already flagged an uncited five-stroke claim; a database behind that figure would
            not have made it measured. */}
        <div className="mt-3 pt-3 border-t border-hg-border grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="proofbank-figures">
          {(summary.figures || []).map((f) => (
            <div key={f.key} data-testid={`proofbank-figure-${f.key}`}>
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">{f.label}</div>
              <div className="font-display text-2xl text-hg-text">
                {f.value === null || f.value === undefined ? '—' : String(f.value)}
              </div>
              <div className="text-[10px] font-sans mt-0.5" style={{ color: QUANT_STYLE.fg }}
                data-testid={`proofbank-provenance-${f.key}`}>
                {f.provenance}{f.n ? ` · n=${f.n}` : ''}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-hg-border flex items-start gap-2" data-testid="proofbank-caveat">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: '#92400E' }} />
          <p className="text-xs font-sans leading-relaxed" style={{ color: '#92400E' }}>{summary.caveat}</p>
        </div>
        {summary.attribution_counts?.not_asked > 0 && (
          <div className="mt-2 flex items-start gap-2" data-testid="proofbank-attribution-warning">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: '#92400E' }} />
            <p className="text-xs font-sans leading-relaxed" style={{ color: '#92400E' }}>
              {summary.attribution_counts.not_asked} of {summary.records} records were never asked
              about their name. {data?.attribution_note}
              {summary.fixable_by_asking > 0 && (
                <> <strong>{summary.fixable_by_asking}</strong> of them have a real quote and can
                be asked — look for “Ask for name” below.</>
              )}
            </p>
          </div>
        )}
      </div>

      {/* ---- filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <label className="flex items-center gap-1.5 text-xs font-sans text-hg-text-dim cursor-pointer select-none" data-testid="proofbank-usable-only">
          <input type="checkbox" checked={usableOnly} onChange={(e) => setUsableOnly(e.target.checked)} className="accent-hg-green w-3.5 h-3.5" />
          Consented &amp; usable only
        </label>
        <button
          onClick={() => setStatusFilter('all')}
          data-testid="proofbank-filter-all"
          className={`text-xs font-sans uppercase tracking-wider rounded-sm px-2.5 py-1 border ${statusFilter === 'all' ? 'border-hg-green text-hg-green bg-hg-green/10' : 'border-hg-border text-hg-text-dim hover:border-hg-green'}`}
        >
          All ({proofs.length})
        </button>
        {['available', 'used', 'withdrawn'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`proofbank-filter-${s}`}
            className="text-xs font-sans uppercase tracking-wider rounded-sm px-2.5 py-1 border"
            style={statusFilter === s
              ? { color: statusStyle(s).fg, backgroundColor: statusStyle(s).bg, borderColor: statusStyle(s).fg }
              : undefined}
          >
            {statusStyle(s).label} ({data?.counts?.[s] ?? 0})
          </button>
        ))}
        <select
          value={minDelta}
          onChange={(e) => setMinDelta(e.target.value)}
          data-testid="proofbank-min-delta"
          className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-xs text-hg-text font-sans"
        >
          <option value="">Any outcome size</option>
          <option value="1">Dropped 1+</option>
          <option value="3">Dropped 3+</option>
          <option value="5">Dropped 5+</option>
        </select>
        <select
          value={fitterFilter}
          onChange={(e) => setFitterFilter(e.target.value)}
          data-testid="proofbank-fitter"
          className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-xs text-hg-text font-sans"
        >
          <option value="all">{fitters.length ? 'Any fitter' : 'No fitter attributed yet'}</option>
          {fitters.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {shown.length === 0 ? (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-10 text-center" data-testid="proofbank-empty">
          <Award className="mx-auto text-hg-text-dim mb-3" />
          <p className="text-hg-text-dim font-sans text-sm">Nothing matches these filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((p) => {
            const u = p.usability || {};
            const att = attributionStyle(p.attribution_state);
            const st = statusStyle(p.status);
            const blk = blockerStyle(u.blocker);
            const busy = busyId === p.id;
            const openAsk = (p.attribution_requests || []).find((a) => a.outcome === 'asked');
            return (
              <div
                key={p.id}
                className={`border rounded-sm p-4 ${p.status === 'withdrawn' ? 'border-red-400/60' : 'border-hg-border'}`}
                style={{ backgroundColor: p.status === 'withdrawn' ? CARD_SURFACES.withdrawn : CARD_SURFACES.default }}
                data-testid="proof-card"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg text-hg-text tracking-wide">
                        {p.first_name || '(no name)'}
                      </span>
                      <span className="text-xs font-sans text-hg-text-dim">{p.email}</span>
                      <Badge fg={st.fg} bg={st.bg}>{st.label}</Badge>
                      {/* The REASON, not just a flag. "no quote text" is empty and nothing
                          will fix it; "quote but no naming permission" is one email away. */}
                      <Badge fg={blk.fg} bg={blk.bg} testid="proof-blocker"
                        title={u.blocker === 'quote_too_short'
                          ? `Comment is ${u.quote_chars} characters; ${data?.min_quote_chars} needed to treat as a quote`
                          : undefined}>
                        {u.blocker_label}
                      </Badge>
                      <Badge fg={att.fg} bg={att.bg} testid="proof-attribution">{att.label}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-hg-text-dim font-sans">
                      {fmtDate(p.captured_at)} · via {p.source_channel || '—'}
                      {p.fitter_name ? <> · fitter <span className="text-hg-text">{p.fitter_name}</span></>
                        : <> · <span className="text-amber-300/80">no fitter attributed</span></>}
                    </div>
                  </div>
                </div>

                {/* ---- QUANTITATIVE PROOF. Its own block, because it is a different kind of
                        proof with a different permission requirement: nothing here quotes or
                        identifies anybody, so it needs no naming consent and is usable now for
                        aggregate statements. Always labelled self-reported. */}
                {u.quantitative_usable && (
                  <div className="mt-3 rounded-sm p-2.5" style={{ backgroundColor: QUANT_STYLE.bg }}
                    data-testid="proof-quant-block">
                    <div className="flex items-center gap-1.5 mb-1">
                      <BarChart3 size={12} style={{ color: QUANT_STYLE.fg }} />
                      <span className="text-[10px] font-sans font-bold uppercase tracking-wider"
                        style={{ color: QUANT_STYLE.fg }}>
                        Usable now for aggregate stats · customer self-reported
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-sans" style={{ color: QUANT_STYLE.fg }}>
                      {p.clubs_performing && <span>clubs: <strong>{p.clubs_performing}</strong></span>}
                      {p.fitter_rating && <span>fitter: <strong>{p.fitter_rating}</strong></span>}
                      {p.handicap_delta !== null && <span>handicap <strong>−{p.handicap_delta}</strong></span>}
                      {p.strokes_dropped_raw && <span>strokes <strong>“{p.strokes_dropped_raw}”</strong></span>}
                    </div>
                  </div>
                )}

                {/* outcome detail */}
                {(p.handicap_delta !== null || p.strokes_dropped_raw) && (
                  <div className="mt-3 flex flex-wrap gap-4 text-sm font-sans" data-testid="proof-outcome">
                    {p.handicap_delta !== null && (
                      <span className="inline-flex items-center gap-1.5 text-hg-text">
                        <TrendingDown size={14} className="text-hg-green" />
                        Handicap {num(p.handicap_before)} → {num(p.handicap_after)}
                        <span className="text-hg-green font-bold">(−{p.handicap_delta})</span>
                      </span>
                    )}
                    {p.strokes_dropped_raw && (
                      <span className="text-hg-text-dim">
                        Strokes, as they wrote it: <span className="text-hg-text">“{p.strokes_dropped_raw}”</span>
                        {p.strokes_dropped_low !== null && (
                          <span className="text-hg-text-dim"> (read as {p.strokes_dropped_low}–{p.strokes_dropped_high})</span>
                        )}
                      </span>
                    )}
                  </div>
                )}

                {/* the words, in full — never truncated */}
                {p.testimonial_text ? (
                  <blockquote className="mt-3 border-l-2 border-hg-green/50 pl-3 text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed" data-testid="proof-quote">
                    {p.testimonial_text}
                  </blockquote>
                ) : (
                  <p className="mt-3 text-xs text-hg-text-dim font-sans italic">No written comment.</p>
                )}
                <div className="mt-1 text-[10px] font-sans text-hg-text-dim">
                  {p.testimonial_source_field ? `from “${p.testimonial_source_field}”` : ''}
                  {p.recommend ? ` · would recommend: ${p.recommend}` : ''}
                  {p.clubs_performing ? ` · clubs: ${p.clubs_performing}` : ''}
                </div>

                {/* ---- the two permissions, each with its own date and its own wording, so
                        "what exactly did this person agree to?" is answerable on the card. */}
                <div className="mt-2 space-y-0.5" data-testid="proof-consents">
                  <div className="text-[11px] font-sans text-hg-text-dim" data-testid="proof-consent-quote">
                    Comments:{' '}
                    {p.consent_quote?.granted || p.testimonial_consent ? (
                      <>granted {fmtDate(p.consent_quote?.at || p.consent_captured_at)} to:{' '}
                        “{p.consent_quote?.wording || p.consent_wording}”</>
                    ) : <span>not granted</span>}
                  </div>
                  <div className="text-[11px] font-sans text-hg-text-dim" data-testid="proof-consent-name">
                    Name:{' '}
                    {p.attribution_state === 'not_asked' ? (
                      <span>never asked — the form these customers saw did not mention their name</span>
                    ) : p.consent_name?.granted ? (
                      <>granted {fmtDate(p.consent_name?.at)} to: “{p.consent_name?.wording}”
                        {p.consent_name?.recorded_out_of_band && ' (recorded by hand)'}</>
                    ) : (
                      <>declined{p.consent_name?.at ? ` ${fmtDate(p.consent_name.at)}` : ''}</>
                    )}
                  </div>
                  {/* Which form version collected each permission, and whether it was migrated
                      rather than collected under the current form. Visible on the card because
                      "what question was this person actually asked?" is the whole point of
                      storing the wording — a version alone would not answer it. */}
                  {(p.consent_quote?.migrated_from_v3 || p.consent_name?.migrated_from_v3) && (
                    <div className="text-[11px] font-sans" style={{ color: QUANT_STYLE.fg }}
                      data-testid="proof-consent-migrated"
                      title={p.consent_name?.note || p.consent_quote?.note}>
                      Migrated from survey v
                      {p.consent_name?.form_version ?? p.consent_quote?.form_version}
                      {' '}— not collected under the current form. Hover for why.
                    </div>
                  )}
                </div>

                {/* ---- the attribution ask, and its answer either way */}
                {(p.attribution_requests || []).length > 0 && (
                  <div className="mt-2 text-[11px] font-sans text-hg-text-dim" data-testid="proof-ask-log">
                    {p.attribution_requests.map((a, i) => (
                      <div key={i}>
                        Name asked {fmtDate(a.asked_at)} by {a.by} ({a.channel}) —{' '}
                        {a.outcome === 'asked' ? <strong>awaiting reply</strong>
                          : a.outcome === 'no_reply' ? 'no reply'
                            : <strong>{a.outcome}</strong>}
                        {a.answered_at ? ` ${fmtDate(a.answered_at)}` : ''}
                      </div>
                    ))}
                  </div>
                )}

                {p.usage?.length > 0 && (
                  <div className="mt-2 text-[11px] font-sans text-hg-text-dim" data-testid="proof-usage">
                    Used: {p.usage.map((u, i) => (
                      <span key={i}>{i ? ' · ' : ''}{u.where} ({fmtDate(u.at)}, {u.by})</span>
                    ))}
                  </div>
                )}
                {p.status === 'withdrawn' && (
                  <div className="mt-2 text-[11px] font-sans text-red-300" data-testid="proof-withdrawn-note">
                    Consent withdrawn {fmtDate(p.withdrawn_at)}
                    {p.withdrawn_reason ? ` — ${p.withdrawn_reason}` : ''}. Usage history kept.
                  </div>
                )}

                {/* actions */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    disabled={!p.testimonial_text}
                    onClick={() => copy(p)}
                    data-testid="proof-copy"
                    className="inline-flex items-center gap-1 text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green disabled:opacity-40"
                  >
                    {copiedId === p.id ? <Check size={12} /> : <Copy size={12} />}
                    {copiedId === p.id ? 'Copied' : 'Copy quote'}
                  </button>
                  {p.is_usable && (
                    <button
                      disabled={busy}
                      onClick={() => markUsed(p)}
                      data-testid="proof-mark-used"
                      className="text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green"
                    >
                      Mark used
                    </button>
                  )}
                  {/* The ONE blocker asking can clear. Offered only when it is genuinely the
                      blocker — a button that appears on a record with no quote would be an
                      invitation to email someone about nothing. */}
                  {u.fixable_by_asking && !openAsk && (
                    <button
                      disabled={busy}
                      onClick={() => openDraft(p)}
                      data-testid="proof-ask-name"
                      className="inline-flex items-center gap-1 text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border"
                      style={{ color: blockerStyle('quote_but_no_naming_permission').fg,
                               borderColor: blockerStyle('quote_but_no_naming_permission').fg }}
                    >
                      <Mail size={12} /> Ask for name
                    </button>
                  )}
                  {openAsk && (
                    <>
                      <span className="text-[11px] font-sans text-hg-text-dim">Reply?</span>
                      <button disabled={busy} onClick={() => recordAnswer(p, 'granted')}
                        data-testid="proof-answer-granted"
                        className="text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green">
                        They said yes
                      </button>
                      <button disabled={busy} onClick={() => recordAnswer(p, 'denied')}
                        data-testid="proof-answer-denied"
                        className="text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-red-500">
                        They said no
                      </button>
                      <button disabled={busy} onClick={() => recordAnswer(p, 'no_reply')}
                        data-testid="proof-answer-noreply"
                        title="Silence is not an answer — this records that we heard nothing and changes no permission."
                        className="text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim">
                        No reply
                      </button>
                    </>
                  )}
                  {p.status !== 'withdrawn' ? (
                    <button
                      disabled={busy}
                      onClick={() => withdraw(p)}
                      data-testid="proof-withdraw"
                      className="inline-flex items-center gap-1 text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-red-400 hover:text-red-300"
                    >
                      <Ban size={12} /> Withdraw consent
                    </button>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() => restore(p)}
                      data-testid="proof-restore"
                      className="inline-flex items-center gap-1 text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green"
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  )}
                  {busy && <Loader2 size={14} className="animate-spin text-hg-green" />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
